import { CSSProperties, ReactElement, ReactNode } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";
import { Icon } from "@sellar-io/components";
import { IconType } from "@sellar-io/components";
import { ParentLink } from "../navigation/ParentLink";

class Props {
  children?: ReactNode;
  to?: string;
  onClick?: () => void;
  promptText?: string;
  className?: string;
  index?: number;
  disabled?: boolean;
  style?: CSSProperties;
  selected?: boolean;
  icon?: keyof typeof IconType | null;
  iconClassName?: string;
  state?: unknown;
  navigation?: "child";
}

const Element = ({
  children,
  to,
  onClick,
  promptText,
  className,
  disabled = false,
  style,
  selected,
  icon = "CHEVRON_RIGHT",
  iconClassName,
}: Props) => {
  const elementIcon = icon ? (
    <Icon icon={icon} className={iconClassName} />
  ) : null;

  return (
    <li
      onClick={() => {
        if (disabled) return;

        if (onClick) {
          onClick();
        }
      }}
      className={twMerge(
        "flex cursor-pointer items-center justify-between overflow-hidden border-b border-solid border-b-zinc-200 bg-white p-4 transition-colors",
        className,
        (onClick || to) && !disabled && !selected && "hover:bg-zinc-50",
        disabled && "cursor-default *:opacity-50",
        selected && "border-r-4 border-solid border-r-black bg-zinc-100",
      )}
      style={style}
    >
      <div className={"flex w-full items-center gap-2"}>{children}</div>
      {!disabled && (onClick || to) && (
        <div
          className={twMerge(
            "flex items-center gap-2 text-sm whitespace-nowrap text-black",
          )}
        >
          <span className={"hidden md:block"}>{promptText}</span>
          {/*allowing icon to be passed as null, so we can include the functionality for on click without a default icon*/}
          {elementIcon}
        </div>
      )}
    </li>
  );
};

export function ListElement({
  children,
  to,
  onClick,
  promptText,
  className,
  disabled = false,
  style,
  selected,
  icon,
  iconClassName,
  state,
  navigation,
}: Props): ReactElement {
  const renderElement = () => (
    <Element
      onClick={onClick}
      to={to}
      promptText={promptText}
      className={className}
      disabled={disabled}
      style={style}
      selected={selected}
      icon={icon}
      iconClassName={iconClassName}
    >
      {children}
    </Element>
  );

  if (to) {
    if (navigation === "child") {
      return (
        <ParentLink to={to} state={state}>
          {renderElement()}
        </ParentLink>
      );
    }

    return (
      <Link to={to} state={state}>
        {renderElement()}
      </Link>
    );
  }
  return renderElement();
}
